import React from 'react';
import {  FiTrendingUp,  FiClock, FiUsers,  FiBarChart2, FiShield, FiGlobe } from 'react-icons/fi';

const ERPLMS = () => {
  const integrationBenefits = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Seamless Student Data Flow',
      description: 'When ERP and LMS are integrated, student data flows seamlessly between systems. Attendance from the ERP automatically appears in the LMS. Grades from the LMS automatically update the ERP. No manual data entry. No data mismatches.',
      stat: 'Eliminates 100% of duplicate data entry',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Unified Student Profile',
      description: 'A single, complete view of every student—academic history, attendance records, performance data, learning activities, and progress. Teachers, administrators, and parents see the same complete picture.',
      stat: '90% of teachers say unified profiles improve student understanding',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Real-Time Academic Analytics',
      description: 'Track student performance in real-time—attendance patterns, assignment completion, test scores, and learning progress. Identify struggling students early and intervene before they fall behind.',
      stat: '85% of institutions use combined data for better decisions',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Reduced Administrative Burden',
      description: 'Teachers spend less time on administrative tasks—no more double data entry, no more switching between systems. One platform for attendance, grading, assignments, and communication.',
      stat: '70% reduction in teacher administrative time',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Better Compliance and Reporting',
      description: 'Academic records are automatically maintained. Compliance reports are generated instantly. Accreditations are supported with accurate, auditable data.',
      stat: '95% of institutions report easier accreditation with integrated systems',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'Improved Student Outcomes',
      description: 'When teachers have complete student data, they can personalize instruction. When administrators have real-time insights, they can allocate resources effectively. The result—better student outcomes.',
      stat: 'Integrated systems improve student outcomes by 30%',
      color: 'bg-blue-50'
    }
  ];

  const standaloneProblems = [
    {
      title: 'Separate Systems, Separate Logins',
      description: 'Teachers, students, and parents must remember different logins for different systems. Integration eliminates this confusion.'
    },
    {
      title: 'Data Duplication and Errors',
      description: 'Entering data twice increases error risk. Integrated systems keep data consistent across platforms.'
    },
    {
      title: 'Manual Data Transfer',
      description: 'Transferring data between systems is time-consuming and error-prone. Integration automates data flow.'
    },
    {
      title: 'No Central Student View',
      description: 'Without integration, student data is scattered. Teachers can\'t see the complete picture.'
    },
    {
      title: 'Higher Administrative Cost',
      description: 'Managing multiple systems increases costs—licenses, training, support, and IT overhead.'
    },
    {
      title: 'Fragmented Learning Experience',
      description: 'Students experience disconnected platforms. They lose engagement and motivation.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        For decades, educational institutions have used separate systems for different functions—one for academic management, another for learning management, another for communication, and yet another for compliance. Each system has its own purpose. Each has its own data. Each has its own login.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">But these separate systems create problems.</strong> Data is scattered. Teachers spend hours entering data twice. Administrators can't get a complete picture. Students experience disconnected platforms. The whole is less than the sum of its parts.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        The solution is simple: <strong className="text-slate-900">integrated ERP + LMS</strong>. The holy grail of education technology. Here's why it matters.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">70%</div>
          <div className="text-sm text-slate-500">Reduction in teacher admin time</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">30%</div>
          <div className="text-sm text-slate-500">Better student outcomes</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">100%</div>
          <div className="text-sm text-slate-500">Elimination of duplicate data entry</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">What ERP + LMS Integration Delivers</h2>

      {/* Benefits List */}
      {integrationBenefits.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Problems with Standalone Systems */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">The Problem with Standalone Systems</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {standaloneProblems.map((item, i) => (
            <div key={i} className="bg-red-50 rounded-xl p-4 border border-red-200">
              <div className="flex items-start gap-2">
                <span className="text-red-500 font-bold text-sm">⚠️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Standalone vs. Integrated ERP + LMS</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Standalone Systems</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Separate logins and interfaces</span></li>
              <li className="flex items-start gap-2">• <span>Manual data entry across systems</span></li>
              <li className="flex items-start gap-2">• <span>Data inconsistencies and errors</span></li>
              <li className="flex items-start gap-2">• <span>Higher administrative costs</span></li>
              <li className="flex items-start gap-2">• <span>Fragmented student experience</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Integrated ERP + LMS</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Single login and unified interface</span></li>
              <li className="flex items-start gap-2">• <span>Automatic data synchronization</span></li>
              <li className="flex items-start gap-2">• <span>Consistent, accurate data</span></li>
              <li className="flex items-start gap-2">• <span>Lower administrative costs</span></li>
              <li className="flex items-start gap-2">• <span>Seamless student experience</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Data Flow */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">How Data Flows in Integrated ERP + LMS</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">ERP</h4>
            <p className="text-xs text-slate-600">Student records, enrollment, attendance, scheduling, fees</p>
            <div className="mt-2 text-indigo-600 text-xs font-bold">↓ Data flows to LMS ↓</div>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">🔄</div>
            <h4 className="font-bold text-slate-900 text-sm">Seamless Integration</h4>
            <p className="text-xs text-slate-600">Real-time data sync, unified user profiles, single login</p>
            <div className="mt-2 text-emerald-600 text-xs font-bold">↓ Data flows to ERP ↓</div>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
            <div className="text-2xl mb-1">📚</div>
            <h4 className="font-bold text-slate-900 text-sm">LMS</h4>
            <p className="text-xs text-slate-600">Courses, assignments, assessments, grades, progress tracking</p>
          </div>
        </div>
        <div className="mt-3 bg-indigo-50 rounded-xl p-3 border border-indigo-200 text-center">
          <p className="text-sm font-semibold text-indigo-700">A Complete Picture of Every Student, Every Teacher, Every Activity</p>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Delivers the Best ERP + LMS Integration</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔗</div>
            <h4 className="font-bold text-slate-900 text-sm">Complete Integration</h4>
            <p className="text-xs text-slate-600">ERP and LMS built on same platform</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Single Login</h4>
            <p className="text-xs text-slate-600">One account for all functions</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Unified Analytics</h4>
            <p className="text-xs text-slate-600">Complete student performance data</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Sync</h4>
            <p className="text-xs text-slate-600">Data updates instantly across systems</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎯</div>
            <h4 className="font-bold text-slate-900 text-sm">NEP 2020 Ready</h4>
            <p className="text-xs text-slate-600">Supports competency-based education</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌐</div>
            <h4 className="font-bold text-slate-900 text-sm">Multi-Device</h4>
            <p className="text-xs text-slate-600">Works on any device, anywhere</p>
          </div>
        </div>
      </div>

      {/* The Holy Grail */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Holy Grail of Education Technology</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          For years, educational technology has been fragmented. Separate systems for separate functions. But the future is integrated. One platform that handles everything—academic management, learning management, communication, and compliance.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>ERP + LMS integration is not just about convenience. It's about effectiveness.</strong> When data flows freely, teachers teach better. Administrators manage better. Students learn better.
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Get Both in One Platform with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          The holy grail of education technology is here. A single platform that combines ERP and LMS—seamless student data flow, unified profiles, real-time analytics, and reduced administrative burden.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon delivers complete ERP + LMS integration. One login. One platform. Complete visibility. Better outcomes.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for integrated ERP + LMS.</strong> The holy grail of education technology. Available now.
        </p>
      </div>
    </>
  );
};

export default ERPLMS;